import React, { useState } from 'react';
import loginImage from '../assets/Alliedlogo.png';
import mountainBackground from '../assets/mountainbackground.png';
import { logIn, resetPassword } from '../firebase/auth';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.js';
import { getCurrentOrganization } from '../utils/organization';
import { toUserMessage } from '../utils/userError';

import '../styling/login.css';

const Login = ({ handleLogin, setStep }) => {
  const organization = getCurrentOrganization();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [userInfo, setUserInfo] = useState({
    email: '',
    password: ''
  });

  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResetMessage('');

    try {
      const message = await resetPassword(resetEmail);
      setResetMessage(message);
      setShowResetPassword(false);
    } catch (error) {
      setError(toUserMessage(error, 'Unable to send a reset email. Please try again.'));
    } finally {
      setLoading(false);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log("Attempting login...");
      const result = await logIn(userInfo.email, userInfo.password);
      console.log("Login result:", result);
    
      // Try to get user document by UID (most common case)
      let userDoc = null;
      try {
        const userDocRef = doc(db, 'users', result.user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          userDoc = userDocSnap;
        }
      } catch (error) {
        console.log("User document not found by UID, checking practices...");
      }
      
      // If not found in users, check practices collection
      if (!userDoc) {
        try {
          const practiceDocRef = doc(db, 'practices', result.user.uid);
          const practiceDocSnap = await getDoc(practiceDocRef);
          if (practiceDocSnap.exists()) {
            userDoc = practiceDocSnap;
          }
        } catch (error) {
          console.log("User document not found in practices either");
        }
      }
  
      if (userDoc) {
          const userData = userDoc.data();
          console.log(userData);
          
          // Check organization access
          const userOrg = userData.organization;
          if (userOrg && userOrg !== organization) {
              // User belongs to a different organization
              const auth = getAuth();
              await auth.signOut();
              setStep("login");
              setError('You do not have access to this tenant. Please use the correct login URL for your organization.');
              return;
          }
  
          if (userData.status === 'Inactive') {
              const auth = getAuth();
              await auth.signOut();
              setStep("login");
              setError('Your account has been deactivated. Please contact an administrator.');
              return;
          }

          if (userData.requiresPasswordChange) {
              setStep('firstLoginPasswordReset');
              return;
          }

          // All checks passed, proceed with login
          handleLogin();
      } else {
          console.error("User document not found in either collection!");
          // Sign out if no user document found
          const auth = getAuth();
          await auth.signOut();
          setError('User account not found. Please contact an administrator.');
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setError(toUserMessage(error, 'Unable to sign in. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    return (
      <>
        <label>
          Email:
          <input
            type="email"
            name="email"
            value={userInfo.email}
            onChange={handleUserInfoChange}
            required
          />
        </label>
        <label>
          Password:
          <input
            type="password"
            name="password"
            value={userInfo.password}
            onChange={handleUserInfoChange}
            required
          />
        </label>
      </>
    );
  };


  // Modern login for development tenant (no branding)
  if (organization === 'development') {
    return (
      <div className="flex min-h-screen flex-col justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Abstract geometric logo for development */}
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900">
            {showResetPassword ? 'Reset your password' : 'Sign in to your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Quality Improvement Reporting
          </p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow-xl ring-1 ring-gray-900/5 sm:rounded-2xl sm:px-12">
            {showResetPassword && (
              <button 
                className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => {
                  setShowResetPassword(false);
                  setResetEmail('');
                  setResetMessage('');
                  setError(null);
                }}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                </svg>
                Back to sign in
              </button>
            )}

            {error && !resetMessage && (
              <div className="mb-6 rounded-lg bg-red-50 p-4 ring-1 ring-red-100">
                <div className="flex">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <p className="ml-3 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {resetMessage && (
              <div className="mb-6 rounded-lg bg-green-50 p-4 ring-1 ring-green-100">
                <div className="flex">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                  </svg>
                  <p className="ml-3 text-sm text-green-700">{resetMessage}</p>
                </div>
              </div>
            )}
            
            {showResetPassword ? (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-900">
                    Email address
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    We'll send you a link to reset your password.
                  </p>
                  <div className="mt-3">
                    <input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      className="block w-full rounded-lg bg-white px-4 py-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm transition-all"
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="flex w-full justify-center rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send reset link'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                    Email address
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={userInfo.email}
                      onChange={handleUserInfoChange}
                      required
                      autoComplete="email"
                      className="block w-full rounded-lg bg-white px-4 py-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                    Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={userInfo.password}
                      onChange={handleUserInfoChange}
                      required
                      autoComplete="current-password"
                      className="block w-full rounded-lg bg-white px-4 py-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm transition-all"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <button 
                    type="button"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                    onClick={() => setShowResetPassword(true)}
                  >
                    Forgot password?
                  </button>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="flex w-full justify-center rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </span>
                  ) : 'Sign in'}
                </button>
              </form>
            )}
          </div>

          <p className="mt-8 text-center text-xs text-gray-500">
            Secure healthcare quality reporting platform
          </p>
        </div>
      </div>
    );
  }

  // Branded login for other tenants (alliedoms, etc.)
  return (
    <div className="login-page-wrapper">
      <div className="mountain-background" style={{backgroundImage: `url(${mountainBackground})`}}></div>
      <div className="login-overlay"></div>
      <div className="login-signup">
        {showResetPassword && (
          <button 
            className="simple-back-button"
            onClick={() => {
              setShowResetPassword(false);
              setResetEmail('');
              setResetMessage('');
              setError(null);
            }}
          >
            ←
          </button>
        )}
        
        <img src={loginImage} alt="login" className="header-image"/>
        <h1>{showResetPassword ? 'Reset Password' : 'Login'}</h1>
  
        {error && !resetMessage && <p className="error-message">{error}</p>}
        {resetMessage && <p className="success-message">{resetMessage}</p>}
        
        {showResetPassword ? (
          <form onSubmit={handleResetPassword}>
            <label>
              Enter your email address and we'll send you a link to reset your password:
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
              />
            </label>
            <button type="submit" className="reset-password-button" disabled={loading}>
              {loading ? 'Sending...' : 'Reset Password'}
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              {renderFormFields()}
              <button type="submit" disabled={loading}>
                {loading ? 'Processing...' : 'Login'}
              </button>
            </form>
            
            <div className="password-reset">
              <button 
                className="reset-password-link" 
                onClick={() => setShowResetPassword(true)}
              >
                Forgot Password?
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );  
};

export default Login;
